import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from 'react-query';
import FormPage from '../../components/ui/FormPage.tsx';
import api from '../../services/api.ts';
import { handlePtBrSubmit, ptBrValidityProps } from '../../utils/htmlValidation.ts';
import { assertNonNegative } from '../../utils/numberGuards.ts';
import { applyPlateMask, isValidPlate, normalizePlate, formatPlate, PLATE_INVALID_MSG } from '../../utils/licensePlate.ts';

const FrotaEditar: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [form, setForm] = useState<any>(null);
  const [error, setError] = useState('');

  const { data, isLoading } = useQuery(
    ['fleet-vehicle', id],
    async () => {
      const res = await api.get(`/patrimonio/frota/${id}`);
      return res.data;
    },
    { enabled: !!id }
  );

  useEffect(() => {
    if (data) {
      setForm({
        make: data.make || '',
        model: data.model || '',
        year: data.year || '',
        licensePlate: applyPlateMask(data.licensePlate || ''),
        color: data.color || '',
        vehicleKind: data.vehicleKind || 'carro',
        fleetStatus: data.fleetStatus || 'active',
        driverName: data.driverName || '',
        estimatedValue: data.estimatedValue ?? '',
        usageLabel: data.usageLabel || '',
        nextPreventiveKm: data.nextPreventiveKm ?? '',
      });
    }
  }, [data]);

  const mutation = useMutation(
    async () => {
      const payload = {
        ...form,
        licensePlate: normalizePlate(form.licensePlate),
        year: Number(form.year),
        estimatedValue: form.estimatedValue === '' ? null : Number(form.estimatedValue),
        nextPreventiveKm: form.nextPreventiveKm === '' ? null : Number(form.nextPreventiveKm),
        driverName: form.driverName || null,
      };
      const res = await api.put(`/patrimonio/frota/${id}`, payload);
      return res.data;
    },
    {
      onSuccess: () => navigate(`/patrimonio/frota/${id}`),
      onError: (err: any) => setError(err?.response?.data?.error || 'Erro ao atualizar'),
    }
  );

  const set = (key: string, value: any) => setForm((prev: any) => ({ ...prev, [key]: value }));

  if (isLoading || !form) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500" />
      </div>
    );
  }

  return (
    <FormPage
      backTo={`/patrimonio/frota/${id}`}
      backLabel="Detalhe do veículo"
      breadcrumb="Patrimônio / Frota / Editar"
      title="Editar veículo"
      subtitle={`${data?.make || ''} ${data?.model || ''} · ${formatPlate(data?.licensePlate || '')}`.trim()}
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={() => navigate(`/patrimonio/frota/${id}`)}>
            Cancelar
          </button>
          <button type="submit" form="frota-edit-form" className="btn btn-primary" disabled={mutation.isLoading}>
            {mutation.isLoading ? 'Salvando…' : 'Salvar alterações'}
          </button>
        </>
      }
    >
      {error && <div className="field-error" style={{ marginBottom: 12 }}>{error}</div>}
      <form
        id="frota-edit-form"
        noValidate
        onSubmit={e =>
          handlePtBrSubmit(e, () => {
            const errs = [
              assertNonNegative(form.nextPreventiveKm, 'Próx. preventiva (km)'),
              assertNonNegative(form.estimatedValue, 'Valor estimado'),
            ].filter(Boolean);
            if (errs.length) {
              setError(errs[0] as string);
              return;
            }
            if (!isValidPlate(form.licensePlate)) {
              setError(PLATE_INVALID_MSG);
              return;
            }
            setError('');
            mutation.mutate();
          })
        }
      >
        <div className="form-section-title">Dados do veículo</div>
        <div className="form-grid">
          <div className="field">
            <label>Marca *</label>
            <input required {...ptBrValidityProps} className="input" value={form.make} onChange={e => set('make', e.target.value)} />
          </div>
          <div className="field">
            <label>Modelo *</label>
            <input required {...ptBrValidityProps} className="input" value={form.model} onChange={e => set('model', e.target.value)} />
          </div>
          <div className="field">
            <label>Ano *</label>
            <input required {...ptBrValidityProps} type="number" className="input" value={form.year} onChange={e => set('year', e.target.value)} />
          </div>
          <div className="field">
            <label>Placa *</label>
            <input required {...ptBrValidityProps} className="input mono" value={form.licensePlate} onChange={e => set('licensePlate', applyPlateMask(e.target.value))} placeholder="ABC-1D23 ou ABC-1234" maxLength={8} />
          </div>
          <div className="field">
            <label>Tipo</label>
            <select className="select" value={form.vehicleKind} onChange={e => set('vehicleKind', e.target.value)}>
              <option value="carro">Carro</option>
              <option value="moto">Moto</option>
              <option value="utilitario">Utilitário</option>
            </select>
          </div>
          <div className="field">
            <label>Status</label>
            <select className="select" value={form.fleetStatus} onChange={e => set('fleetStatus', e.target.value)}>
              <option value="active">Ativo</option>
              <option value="maintenance">Em manutenção</option>
              <option value="inactive">Inativo</option>
            </select>
          </div>
          <div className="field">
            <label>Cor</label>
            <input className="input" value={form.color} onChange={e => set('color', e.target.value)} />
          </div>
          <div className="field">
            <label>Condutor</label>
            <input className="input" value={form.driverName} onChange={e => set('driverName', e.target.value)} />
          </div>
          <div className="field">
            <label>Próx. preventiva (km)</label>
            <input type="number" inputMode="numeric" {...ptBrValidityProps} className="input" value={form.nextPreventiveKm} onChange={e => set('nextPreventiveKm', e.target.value)} />
          </div>
          <div className="field">
            <label>Valor estimado (R$)</label>
            <input type="number" inputMode="decimal" step="0.01" {...ptBrValidityProps} className="input" value={form.estimatedValue} onChange={e => set('estimatedValue', e.target.value)} />
          </div>
          <div className="field full">
            <label>Uso</label>
            <input className="input" value={form.usageLabel} onChange={e => set('usageLabel', e.target.value)} />
          </div>
        </div>
      </form>
    </FormPage>
  );
};

export default FrotaEditar;
